import { Box, FormHelperText, Grid2 } from '@mui/material';
import BackIcon from '@assets/images/BackIcon.svg';
import CustomIconButton from '@ui-components/IconButton/CustomIconButton';
import CustomTypography from '@ui-components/Typography/CustomTypography';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  CONSULTS_MORE_DETAILS_ROUTE_PATH,
  CONSULTS_ROUTE_PATH,
} from '../../routers/routePaths';
import CustomAutoCompleteSingleSelection from '@ui-components/AutoComplete/CustomAutoCompleteSingleSelection';
import CustomSelect from '@ui-components/Select/CustomSelect';
import CustomDivider from '@ui-components/Divider/CustomDivider';
import {
  type ConsultResponse,
  useGetConsultDetailsById,
  useGetConsultsMetaData,
  useGetIntakeFormForConsult,
  useSaveOrUpdateConsult,
} from '../../API/hooks/ConsultsHook';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import FormField, {
  TEXT_FIELD_ALLOWED_CHARACTERS_PATTERN,
} from '@dynamicForm/FormFields';
import CustomButton from '@ui-components/Button/CustomButton';
import type {
  ConsultForm,
  ConsultObservation,
} from '../../API/services/ConsultsService';
import ConfirmDiscardDialog from '../components/ConfirmDiscardDialog';
import CustomDialog from '@qler-view/shared_lib/src/shared/lib/ui-components/Dialog/CustomDialog';
import CustomDialogContent from '@ui-components/Dialog/CustomDialogContent';
import CustomDialogActions from '@ui-components/Dialog/CustomDialogActions';
import moment from 'moment';
import CancelConsultModel, {
  type CancelReasonFacilitySubmitParams,
  type CancelReasonFacilityParams,
} from '@qler-view/shared_lib/src/shared/statusrequests/CancelConsultModel';
import CompleteConsultModel from '@qler-view/shared_lib/src/shared/statusrequests/CompleteConsultModel';
import CustomSnackbar from '@qler-view/shared_lib/src/shared/lib/ui-components/Snackbar/CustomSnackbar';
import {
  useCancelConsultMetadata,
  useCompleteConsult,
  useCompleteDDLDetails,
  useOnHoldConsultMetadata,
  useSaveCancelConsult,
  useSaveOnHoldConsult,
} from '../../shared/statusRequest/StatusRequestSetup';
import { PORTAL_TYPES } from '@qler-view/shared_lib/src';
import { type CompleteConsultParams } from '@qler-view/shared_lib/src/shared/statusrequests/statusRequestService';
import OnHoldConsultModel, {
  type OnHoldReasonFacilityParams,
} from './../../../../../shared_lib/src/shared/statusrequests/OnHoldConsultModel';
import CustomTextField from '@qler-view/shared_lib/src/shared/lib/ui-components/TextField/CustomTextField';
import EditOnlyIcon from '@assets/images/editOnly.svg';
import CustomTooltip from '@qler-view/shared_lib/src/shared/lib/ui-components/ToolTip/CustomToolTip';

export interface ConsultFormValues {
  facilityId: number;
  sessionStatusId: number;
  sessionId: string;

  patientFirstName: string;
  patientLastName: string;
  dateOfBirth: string;
  patientMrn: string;
  callBackNumber: string;
  reasonForConsult: string;
  unitOrDepartment: string;
  referringProvider: string;
  cart: string;
  notes: string;

  id: number;
  fieldName: string;
  fieldValues: string | number;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const AddNewConsult = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = localStorage.getItem('userId') ?? '0';
  const initialSessionStatusRef = useRef<number | null>(null);
  const { consultId } = useParams<{ consultId?: string }>();

  const [facilityId, setFacilityId] = useState<number>(0);

  const [openConsultConfirmDialog, setOpenConsultConfirmDialog] =
    useState(false);
  const [openCancelConsult, setOpenCancelConsult] = useState(false);
  const [openCompleteConsult, setOpenCompleteConsult] = useState(false);
  const [openOnHoldConsult, setOpenOnHoldConsult] = useState(false);

  const { ConsultsMetaData } = useGetConsultsMetaData(
    userId,
    Number(consultId ?? 0)
  );

  const statusByName = useMemo(() => {
    const map: Record<string, number> = {};

    (ConsultsMetaData?.updateStatusList ?? []).forEach((s) => {
      map[s.name.toUpperCase().replaceAll(/\s+/g, '_')] = s.id;
    });

    return map;
  }, [ConsultsMetaData?.updateStatusList]);

  const [sessionStatus, setSessionStatus] = useState<number>(statusByName.NEW);
  const [previousStatus, setPreviousStatus] = useState<number | null>(null);
  const [previousAssignedTo, setPreviousAssignedTo] = useState<number | null>(
    null
  );
  const [activePopupStatus, setActivePopupStatus] = useState<number | null>(
    null
  );
  const isCompletePopupFromEdit = useRef<boolean>(false);

  const isNewStatus = sessionStatus === statusByName.NEW;
  const isOnHoldStatus = sessionStatus === statusByName.ON_HOLD;

  const isCompleted = sessionStatus === statusByName.COMPLETED;

  const isCanceled = sessionStatus === statusByName.CANCELED;

  const isAssignedToRequired = !isNewStatus && !isCanceled && !isOnHoldStatus;

  const isAssignedToDisabled = isNewStatus || isOnHoldStatus;

  const { CancelConsultMetadata: ConsultCancelMetaData } =
    useCancelConsultMetadata(PORTAL_TYPES.OPERATIONS);
  const { completeDDLDetails } = useCompleteDDLDetails(
    PORTAL_TYPES.OPERATIONS,
    consultId ?? ''
  );

  const { OnHoldConsultMetadata } = useOnHoldConsultMetadata(
    PORTAL_TYPES.OPERATIONS
  );

  const [cancelConsultValues, setCancelConsultValues] =
    useState<CancelReasonFacilityParams>({} as CancelReasonFacilityParams);
  const { mutate: cancelConsult, isPending: isCancelling } =
    useSaveCancelConsult();
  const { mutate: completeConsult, isPending: isCompleting } =
    useCompleteConsult(PORTAL_TYPES.OPERATIONS);
  const { mutate: onHoldConsult, isPending: isOnHolding } =
    useSaveOnHoldConsult();
  const [completeConsultValues, setCompleteConsultValues] =
    useState<CompleteConsultParams>({} as CompleteConsultParams);
  const [showConsultObservationMessage, setShowConsultObservationMessage] =
    useState(false);
  const [apiConsultObservation, setApiConsultObservation] =
    useState<ConsultObservation | null>(null);

  const [onHoldConsultValues, setOnHoldConsultValues] =
    useState<OnHoldReasonFacilityParams>({} as OnHoldReasonFacilityParams);

  useEffect(() => {
    if (
      consultId &&
      initialSessionStatusRef.current === null &&
      sessionStatus !== undefined
    ) {
      initialSessionStatusRef.current = sessionStatus;
    }
  }, [consultId, sessionStatus]);

  const isSessionStatusDisabled =
    Boolean(consultId) &&
    initialSessionStatusRef.current === statusByName.COMPLETED;

  const [committedFacilityId, setCommittedFacilityId] = useState<number | null>(
    Number(facilityId) || null
  );

  const [pendingFacilityId, setPendingFacilityId] = useState<number | null>(
    null
  );
  const [showFacilityConfirm, setShowFacilityConfirm] = useState(false);
  const [savedFormValues, setSavedFormValues] =
    useState<Partial<ConsultFormValues> | null>(null);

  const [openToast, setOpenToast] = useState(false);
  const [message, setMessage] = useState('');
  const [toastType, setToastType] = useState<'error' | 'success' | 'info'>(
    'error'
  );

  const { DynamicIntakeForm, isLoading: DynamicIntakeFormLoading } =
    useGetIntakeFormForConsult(userId, facilityId, location?.state?.consultId);

  const onSuccess = (response: ConsultResponse) => {
    const toast = consultId ? 'update' : 'success';
    const goToList = () => navigate(`${CONSULTS_ROUTE_PATH}?toast=${toast}`);
    if (consultId) {
      if (sessionStatus === statusByName.CANCELED) {
        return handleSubmitCancelReason(goToList);
      }
      if (sessionStatus === statusByName.COMPLETED) {
        if (
          showConsultObservationMessage &&
          Object.keys(completeConsultValues).length > 0
        ) {
          return handleSubmitCompleteForm(goToList);
        }
      } else if (sessionStatus === statusByName.ON_HOLD) {
        return handleSubmitOnHoldReason(Number(consultId), goToList);
      }
    } else if (sessionStatus === statusByName.ON_HOLD) {
      return handleSubmitOnHoldReason(response.id, goToList);
    }
    goToList();
  };

  const { mutate: saveOrUpdateConsult, isPending: isSaving } =
    useSaveOrUpdateConsult(onSuccess);

  const [consultSubmitAction, setConsultSubmitAction] = useState<string>('');

  const { ConsultDetailsById, isPending: isConsultDetailsLoading } =
    useGetConsultDetailsById(userId, Number(consultId));

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    clearErrors,
    watch,
    getValues,
  } = useForm<ConsultFormValues>({
    mode: 'onChange',
    defaultValues: {
      facilityId: 0,
      sessionStatusId: 0,
      assignedTo: ConsultDetailsById?.consultAssignedToProviderId ?? 0,
      consultAssignedToProviderId:
        ConsultDetailsById?.consultAssignedToProviderId ?? 0,
      patientFirstName: '',
      patientLastName: '',
      dateOfBirth: '',
      patientMrn: '',
      callBackNumber: '',
      reasonForConsult: '',
      unitOrDepartment: '',
      referringProvider: '',
      cart: '',
      notes: '',
    },
  });

  const currentUnitOrDepartment = watch('unitOrDepartment');

  const mapConsultToFormValues = (
    consult: ConsultForm
  ): Partial<ConsultFormValues> => {
    const base = {
      facilityId: consult.facilityId,
      sessionStatusId: consult.sessionStatusId,
      sessionId: consult.sessionId,
      assignedTo: consult.consultAssignedToProviderId,
      consultAssignedToProviderId: consult.consultAssignedToProviderId,
      patientFirstName: consult.sessionFields.patientFirstName,
      patientLastName: consult.sessionFields.patientLastName,
      dateOfBirth: consult.sessionFields.dateOfBirth,
      patientMrn: consult.sessionFields.patientMrn,
      callBackNumber: consult.sessionFields.callBackNumber,
      reasonForConsult: consult.sessionFields.reasonForConsult,
      unitOrDepartment: consult.sessionFields.unitOrDepartment,
      referringProvider: consult.sessionFields.referringProvider,
      cart: consult.sessionFields.cart ?? '',
      notes: consult.notes,
    };

    const dynamicFields = consult.additionalSessionFields?.reduce<
      Record<string, string | number>
    >((acc, f) => {
      acc[f.fieldName] = String(f.fieldValue ?? '');
      return acc;
    }, {});

    return { ...base, ...dynamicFields };
  };

  useEffect(() => {
    if (!ConsultDetailsById) return;
    setFacilityId(ConsultDetailsById.facilityId);
    setCommittedFacilityId(ConsultDetailsById.facilityId);
    setValue('facilityId', ConsultDetailsById.facilityId);
    setValue('sessionStatusId', ConsultDetailsById.sessionStatusId);
    setValue('assignedTo', ConsultDetailsById.consultAssignedToProviderId);
    setSessionStatus(ConsultDetailsById.sessionStatusId);
    setPreviousStatus(ConsultDetailsById.sessionStatusId);
    setPreviousAssignedTo(ConsultDetailsById.consultAssignedToProviderId);

    if (ConsultDetailsById.consultObservation) {
      setApiConsultObservation(ConsultDetailsById.consultObservation);
    }
  }, [ConsultDetailsById]);

  useEffect(() => {
    if (!ConsultDetailsById || !DynamicIntakeForm) return;

    // If we have saved form values (from facility change in edit mode), merge them
    if (savedFormValues && consultId) {
      const baseFormValues = mapConsultToFormValues(ConsultDetailsById);
      // Exclude facilityId from saved values - use current facilityId state instead

      const { facilityId, ...savedValuesWithoutFacilityId } = savedFormValues;
      const mergedValues = {
        ...baseFormValues,
        ...savedValuesWithoutFacilityId,
        facilityId,
      };
      reset(mergedValues);
      setSavedFormValues(null);
    } else {
      const formValues = mapConsultToFormValues(ConsultDetailsById);
      // In edit mode, always use current facilityId state (not from API)
      if (consultId && facilityId !== ConsultDetailsById.facilityId) {
        formValues.facilityId = facilityId;
      }
      reset(formValues);
    }
  }, [
    DynamicIntakeForm,
    ConsultDetailsById,
    reset,
    savedFormValues,
    consultId,
    facilityId,
  ]);

  useEffect(() => {
    if (isNewStatus) {
      setValue('assignedTo', null, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [isNewStatus, setValue]);

  const getRecommendationValue = (): string => {
    if (!completeConsultValues.billable) return '';

    if (completeConsultValues.recommendation) {
      return (
        completeDDLDetails?.recommendations?.find(
          (r) => r.id === completeConsultValues.recommendation
        )?.name || ''
      );
    }
    return apiConsultObservation?.recommendation || '';
  };

  const getDiagnosisValue = (): string => {
    if (!completeConsultValues.billable) return '';

    if (
      completeConsultValues.diagnosis &&
      completeConsultValues.diagnosis.length > 0
    ) {
      return completeConsultValues.diagnosis.map((d) => d.name).join(', ');
    }
    return apiConsultObservation?.diagnosis || '';
  };

  const getEmCodesValue = (): string => {
    if (!completeConsultValues.billable) return '';

    if (completeConsultValues.emCodes) {
      return (
        completeDDLDetails?.emCodes?.find(
          (e) => e.id === completeConsultValues.emCodes
        )?.name || ''
      );
    }
    return apiConsultObservation?.emCodes || '';
  };

  const getFollowUpValue = (): string => {
    if (completeConsultValues.followUp) {
      return (
        completeDDLDetails?.followUp?.find(
          (f) => f.id === completeConsultValues.followUp
        )?.name || ''
      );
    }
    return apiConsultObservation?.followUp || '';
  };

  const formatFieldValue = (fieldValue: unknown, fieldType: string): string => {
    if (!fieldValue) return '';

    if (fieldType === 'Date') {
      return moment.utc(fieldValue).format('YYYY-MM-DD') + 'T00:00:00.000Z';
    }

    return fieldValue as string;
  };

  const buildAdditionalSessionFields = (values: ConsultFormValues) => {
    return (
      DynamicIntakeForm?.additionalFields.map((field) => {
        const fieldValue = values[field.fieldName as keyof ConsultFormValues];
        const formattedValue = formatFieldValue(fieldValue, field.type);

        return {
          id: Number(field.id),
          fieldName: field.fieldName,
          fieldValue: formattedValue,
        };
      }) ?? []
    );
  };

  const buildConsultObservation = (
    recommendationValue: string,
    diagnosisValue: string,
    emCodesValue: string,
    isBillableConsultValue: boolean,
    isSurgeValue: boolean,
    unitOrDepartmentValue: string,
    followUpValue: string
  ): ConsultObservation | undefined => {
    if (Object.keys(completeConsultValues).length === 0) {
      return apiConsultObservation ?? undefined;
    }

    return {
      recommendation: recommendationValue,
      diagnosis: diagnosisValue,
      emCodes: emCodesValue,
      isBillableConsult: isBillableConsultValue,
      isSurge: isSurgeValue,
      opsComments:
        completeConsultValues.opsReasonForCompletion ||
        apiConsultObservation?.opsComments ||
        '',
      unitOrDepartment: unitOrDepartmentValue,
      followUp: followUpValue,
    };
  };

  const onSubmit: SubmitHandler<ConsultFormValues> = (values): void => {
    const recommendationValue = getRecommendationValue();
    const diagnosisValue = getDiagnosisValue();
    const emCodesValue = getEmCodesValue();
    const followUpValue = getFollowUpValue();

    const isBillableConsultValue =
      completeConsultValues.billable ??
      apiConsultObservation?.isBillableConsult ??
      false;

    const isSurgeValue =
      completeConsultValues.isSurge ?? apiConsultObservation?.isSurge ?? false;

    const payload: ConsultForm = {
      id: consultId ? Number(consultId) : 0,
      facilityId: values.facilityId,
      notes: values.notes,
      sessionStatusId:
        values.sessionStatusId && values.sessionStatusId > 0
          ? values.sessionStatusId
          : statusByName.NEW,
      consultAssignedToProviderId: values.assignedTo,
      sessionId: consultSubmitAction === 'update' ? values.sessionId : '',
      sessionFields: {
        patientFirstName: values.patientFirstName,
        patientLastName: values.patientLastName,
        dateOfBirth: values.dateOfBirth
          ? moment.utc(values.dateOfBirth).format('YYYY-MM-DD') +
            'T00:00:00.000Z'
          : '',
        patientMrn: values.patientMrn,
        callBackNumber: values.callBackNumber,
        reasonForConsult: values.reasonForConsult,
        unitOrDepartment: values.unitOrDepartment,
        referringProvider: values.referringProvider,
        cart: values.cart,
      },
      additionalSessionFields: buildAdditionalSessionFields(values),
      consultObservation: buildConsultObservation(
        recommendationValue,
        diagnosisValue,
        emCodesValue,
        isBillableConsultValue,
        isSurgeValue,
        values.unitOrDepartment,
        followUpValue
      ),
    };

    if (consultSubmitAction) {
      saveOrUpdateConsult({
        userId,
        action: consultSubmitAction,
        ...payload,
      });
    }
  };

  const handleOpenConsultConfirmDialog = (state: boolean) => {
    setOpenConsultConfirmDialog(state);
  };

  const applyFacilityChange = (newFacilityId: number | null) => {
    const facilityIdValue = newFacilityId ?? 0;
    setCommittedFacilityId(newFacilityId);
    setFacilityId(facilityIdValue);
    setValue('facilityId', facilityIdValue);

    setShowFacilityConfirm(false);
    setPendingFacilityId(null);
    // Don't clear savedFormValues here - it will be used when new intake form loads
  };

  const cancelFacilityChange = () => {
    setShowFacilityConfirm(false);
    setPendingFacilityId(null);
    setSavedFormValues(null);
  };

  useEffect(() => {
    if (!isAssignedToRequired) {
      clearErrors('assignedTo');
    }
  }, [isAssignedToRequired, clearErrors]);

  const handleConsultBackButtonClick = () => {
    if (consultId && !location.state?.isConsultList) {
      navigate(CONSULTS_MORE_DETAILS_ROUTE_PATH + `/${consultId}`);
    } else {
      navigate(CONSULTS_ROUTE_PATH, { replace: true });
    }
  };

  let buttonText: string;

  const isSubmitting = isSaving || isCancelling || isCompleting || isOnHolding;

  if (isSubmitting) {
    buttonText = 'Submitting...';
  } else if (consultId) {
    buttonText = 'Update';
  } else {
    buttonText = 'Submit';
  }

  const handleSubmitCancelReason = (onDone?: () => void) => {
    const mappedTechnicalDifficultiesId =
      [
        cancelConsultValues.patientAdmittedId,
        cancelConsultValues.noLongerRequiredId,
        cancelConsultValues.patientDischargedId,
        cancelConsultValues.technicalDifficultiesId,
      ].find((id) => id !== 0) ?? 0;

    const payload: CancelReasonFacilitySubmitParams = {
      cancelReasonId: cancelConsultValues.cancelReasonId,
      technicalDifficultiesId: mappedTechnicalDifficultiesId,
      otherReason: cancelConsultValues.otherReason,
    };
    cancelConsult(
      {
        userId: userId.toString(),
        portalType: PORTAL_TYPES.OPERATIONS,
        consultId: Number(consultId),
        ...payload,
      },
      {
        onSuccess: () => {
          setOpenCancelConsult(false);
          onDone?.();
        },
      }
    );
  };

  const handleSubmitCompleteForm = (onDone?: () => void) => {
    const isBillable = completeConsultValues.billable;
    completeConsult(
      {
        userId: Number(userId),
        consultId: Number(consultId),
        billable: isBillable,
        diagnosis: isBillable ? completeConsultValues.diagnosis : [],
        recommendation: isBillable ? completeConsultValues.recommendation : 0,
        emCodes: isBillable ? completeConsultValues.emCodes : 0,
        isSurge: completeConsultValues.isSurge,
        opsReasonForCompletion: completeConsultValues.opsReasonForCompletion,
        unitOrDepartment: completeConsultValues.unitOrDepartment,
        followUp: completeConsultValues.followUp ?? 0,
      },
      {
        onSuccess: () => onDone?.(),
      }
    );
  };

  const handleSubmitOnHoldReason = (consultId: number, onDone?: () => void) => {
    onHoldConsult(
      {
        userId: userId.toString(),
        portalType: PORTAL_TYPES.OPERATIONS,
        consultId: consultId,
        ...onHoldConsultValues,
      },
      {
        onSuccess: () => {
          setOpenOnHoldConsult(false);
          if (consultId) {
            setValue('assignedTo', previousAssignedTo ?? 0, {
              shouldValidate: true,
            });
          }
          onDone?.();
        },
      }
    );
  };

  const getPreviousName = () => {
    const statusEntry = Object.entries(statusByName).find(
      ([, value]) => value === previousStatus
    );
    const statusName = statusEntry?.[0];

    const formattedStatus = statusName
      ? statusName
          .replaceAll('_', ' ')
          .toLowerCase()
          .replaceAll(/\b\w/g, (c) => c.toUpperCase())
      : 'New';

    setMessage(
      `The consult has been reverted to its previous status "${formattedStatus}".`
    );
    setToastType('error');
    setOpenToast(true);
    setSessionStatus(previousStatus ?? statusByName.NEW);
    setValue('sessionStatusId', previousStatus ?? 0, {
      shouldValidate: true,
    });
  };

  const closeAllPopups = () => {
    setOpenCancelConsult(false);
    setOpenCompleteConsult(false);
    setOpenOnHoldConsult(false);
  };

  const resetAllConsultValues = () => {
    setCancelConsultValues({} as CancelReasonFacilityParams);
    setCompleteConsultValues({} as CompleteConsultParams);
    setOnHoldConsultValues({} as OnHoldReasonFacilityParams);
    setShowConsultObservationMessage(false);
  };

  const getPatientName = () => {
    if (!isConsultDetailsLoading && ConsultDetailsById?.sessionFields) {
      return `(${ConsultDetailsById.sessionFields.patientFirstName} ${ConsultDetailsById.sessionFields.patientLastName})`;
    }
    return '';
  };

  const handleEditConsultsObservationInfo = () => {
    isCompletePopupFromEdit.current = true;
    setOpenCompleteConsult(true);
  };

  const getDisplayValue = (
    value: string | number | boolean | undefined | null
  ) => {
    if (value === undefined || value === null || value === '') return 'N/A';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const getDiagnosisNames = () => {
    const isBillable =
      completeConsultValues.billable ??
      apiConsultObservation?.isBillableConsult ??
      false;

    if (!isBillable) return 'N/A';

    if (
      completeConsultValues.diagnosis &&
      completeConsultValues.diagnosis.length > 0
    ) {
      return completeConsultValues.diagnosis.map((d) => d.name).join(', ');
    }
    if (apiConsultObservation?.diagnosis) {
      return apiConsultObservation.diagnosis;
    }
    return 'N/A';
  };

  const getRecommendationName = () => {
    const isBillable =
      completeConsultValues.billable ??
      apiConsultObservation?.isBillableConsult ??
      false;

    if (!isBillable) return 'N/A';

    if (
      completeConsultValues.recommendation &&
      completeDDLDetails?.recommendations
    ) {
      const rec = completeDDLDetails.recommendations.find(
        (r) => r.id === completeConsultValues.recommendation
      );
      if (rec) return rec.name;
    }
    if (apiConsultObservation?.recommendation) {
      return apiConsultObservation.recommendation;
    }
    return 'N/A';
  };

  const getEmCodeName = () => {
    const isBillable =
      completeConsultValues.billable ??
      apiConsultObservation?.isBillableConsult ??
      false;

    if (!isBillable) return 'N/A';

    if (completeConsultValues.emCodes && completeDDLDetails?.emCodes) {
      const emCode = completeDDLDetails.emCodes.find(
        (e) => e.id === completeConsultValues.emCodes
      );
      if (emCode) return emCode.name;
    }
    if (apiConsultObservation?.emCodes) {
      return apiConsultObservation.emCodes;
    }
    return 'N/A';
  };

  // Helper functions to convert API string names to IDs for dropdown population
  const getRecommendationIdFromName = (name: string): number => {
    if (!completeDDLDetails?.recommendations) return 0;
    const rec = completeDDLDetails.recommendations.find(
      (r) => r.name.toLowerCase() === name.toLowerCase()
    );
    return rec ? rec.id : 0;
  };

  const getEmCodeIdFromName = (name: string): number => {
    if (!completeDDLDetails?.emCodes) return 0;
    const emCode = completeDDLDetails.emCodes.find(
      (e) => e.name.toLowerCase() === name.toLowerCase()
    );
    return emCode ? emCode.id : 0;
  };

  const getFollowUpIdFromName = (name?: string): number => {
    if (!name || !completeDDLDetails?.followUp) return 0;
    const followUp = completeDDLDetails.followUp.find(
      (f) => f.name.toLowerCase() === name.toLowerCase()
    );
    return followUp ? followUp.id : 0;
  };

  const getDiagnosisArrayFromString = (diagnosisString: string) => {
    if (!completeDDLDetails?.diagnosis || !diagnosisString) return [];
    const diagnosisNames = diagnosisString.split(',').map((d) => d.trim());
    return completeDDLDetails.diagnosis.filter((d) =>
      diagnosisNames.some((name) => name.toLowerCase() === d.name.toLowerCase())
    );
  };

  return (
    <Box>
      {openToast && (
        <CustomSnackbar
          id="toast-consult-list"
          successOrError={toastType}
          open={openToast}
          handleClose={() => setOpenToast(false)}
          toastrMessage={message}
        />
      )}
      {openCancelConsult && (
        <CancelConsultModel
          openCancelConsultPopup={openCancelConsult}
          onCloseCancelConsultPopup={() => {
            setOpenCancelConsult(false);
            if (Object.keys(cancelConsultValues).length === 0) {
              getPreviousName();
            }
          }}
          ConsultCancelMetaData={ConsultCancelMetaData ?? undefined}
          onSubmitCancelConsult={(values) => {
            setCancelConsultValues(values);

            setOpenCancelConsult(false);
          }}
          initialValues={cancelConsultValues}
          portal={PORTAL_TYPES.OPERATIONS}
        />
      )}
      {openCompleteConsult &&
        (() => {
          let completeConsultInitialValues = null;

          if (Object.keys(completeConsultValues).length > 0) {
            completeConsultInitialValues = {
              ...completeConsultValues,
              unitOrDepartment:
                currentUnitOrDepartment ??
                completeConsultValues.unitOrDepartment,
            };
          } else if (apiConsultObservation) {
            const isBillable = apiConsultObservation.isBillableConsult;
            completeConsultInitialValues = {
              userId: Number(userId),
              consultId: Number(consultId),
              billable: isBillable,
              diagnosis:
                isBillable && completeDDLDetails
                  ? getDiagnosisArrayFromString(apiConsultObservation.diagnosis)
                  : [],
              recommendation:
                isBillable && completeDDLDetails
                  ? getRecommendationIdFromName(
                      apiConsultObservation.recommendation
                    )
                  : 0,
              emCodes:
                isBillable && completeDDLDetails
                  ? getEmCodeIdFromName(apiConsultObservation.emCodes)
                  : 0,
              isSurge: apiConsultObservation.isSurge,
              followUp: getFollowUpIdFromName(apiConsultObservation.followUp),
              opsReasonForCompletion: apiConsultObservation.opsComments || '',
              unitOrDepartment:
                currentUnitOrDepartment ??
                (apiConsultObservation.unitOrDepartment || ''),
            };
          } else {
            completeConsultInitialValues = {
              userId: Number(userId),
              consultId: Number(consultId),
              billable: true,
              diagnosis: [],
              recommendation: 0,
              emCodes: 0,
              isSurge: false,
              followUp: 0,
              opsReasonForCompletion: '',
              unitOrDepartment: currentUnitOrDepartment ?? '',
            };
          }

          return (
            <CompleteConsultModel
              openCompletePopup={openCompleteConsult}
              onCloseCompletePopup={() => {
                setOpenCompleteConsult(false);
                if (
                  Object.keys(completeConsultValues).length === 0 &&
                  !isCompletePopupFromEdit.current
                ) {
                  getPreviousName();
                }
                isCompletePopupFromEdit.current = false;
              }}
              completeDDLDetails={completeDDLDetails}
              onSubmitCompleteConsult={(values) => {
                setCompleteConsultValues(values);
                setValue('unitOrDepartment', values.unitOrDepartment, {
                  shouldValidate: true,
                  shouldDirty: true,
                });
                setOpenCompleteConsult(false);
                setShowConsultObservationMessage(true);
              }}
              portalType={PORTAL_TYPES.OPERATIONS}
              initialValues={completeConsultInitialValues}
            />
          );
        })()}

      {openOnHoldConsult && (
        <OnHoldConsultModel
          openOnHoldConsultPopup={openOnHoldConsult}
          onCloseOnHoldConsultPopup={() => {
            setOpenOnHoldConsult(false);
            if (Object.keys(onHoldConsultValues).length === 0) {
              getPreviousName();
              if (consultId) {
                setValue('assignedTo', previousAssignedTo ?? 0, {
                  shouldValidate: true,
                });
              }
            }
          }}
          ConsultOnHoldMetaData={OnHoldConsultMetadata ?? undefined}
          onSubmitOnHoldConsult={(values: OnHoldReasonFacilityParams) => {
            setOnHoldConsultValues(values);

            setOpenOnHoldConsult(false);
          }}
          initialValues={onHoldConsultValues}
          portal={PORTAL_TYPES.OPERATIONS}
        />
      )}
      <Box className="pt-20 pb-20">
        <Grid2 container>
          <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
            <Box className="text-align-left display-flex">
              <CustomIconButton
                id="back-button"
                icon={<BackIcon />}
                onClick={() => {
                  handleConsultBackButtonClick();
                }}
              ></CustomIconButton>

              <CustomTypography
                className="pl-8"
                variant="h4"
                id="add-new-facility-user-text"
              >
                {consultId
                  ? 'Edit Consult Request'
                  : 'Create New Consult Request'}
              </CustomTypography>
              <CustomTypography
                className="pl-12"
                variant="h4"
                id="partner-business-id"
              >
                {getPatientName()}
              </CustomTypography>
            </Box>
          </Grid2>
        </Grid2>
      </Box>

      <Box className="form-background p-24">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Box className="display-flex">
            <Grid2 container spacing={4} className="width-80">
              <Grid2 container spacing={4} className="width-80">
                <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                  <CustomTypography
                    variant="h5"
                    className="text-align-left pb-4"
                    id="consult-information-text"
                  >
                    Consult Information
                  </CustomTypography>
                </Grid2>
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                <CustomTypography
                  id="facility-name-label"
                  variant="body3"
                  color="textPrimary"
                  className="text-align-left pb-12"
                >
                  Facility Name&nbsp;<span className="color-red">*</span>
                </CustomTypography>
                <CustomAutoCompleteSingleSelection
                  id="facility-name"
                  placeholder="Select Facility Name"
                  size="small"
                  value={
                    ConsultsMetaData?.facilityList?.find(
                      (f) => f.id === facilityId
                    ) ?? null
                  }
                  options={ConsultsMetaData?.facilityList ?? []}
                  onChange={(selectedFacility) => {
                    if (!selectedFacility) {
                      return;
                    }

                    const selectedFacilityId = Number(selectedFacility.id);

                    // First time selection – no confirm needed
                    if (!committedFacilityId) {
                      applyFacilityChange(selectedFacilityId);
                      return;
                    }

                    // User picked the same facility → do nothing
                    if (selectedFacilityId === committedFacilityId) return;

                    // User re-selected a different facility → ask for confirmation
                    // Save current form values if in edit mode
                    if (consultId) {
                      const currentValues = getValues();
                      setSavedFormValues(currentValues);
                    }
                    setPendingFacilityId(selectedFacilityId);
                    setShowFacilityConfirm(true);
                    reset();
                  }}
                  disabled={sessionStatus !== statusByName.NEW && !!consultId}
                  noOptionsText="No Facility Found"
                />
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                {DynamicIntakeForm && (
                  <Box>
                    <CustomTypography
                      id="consult-state-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Consult Status&nbsp;<span className="color-red">*</span>
                    </CustomTypography>

                    <CustomSelect
                      id="consult-state"
                      placeholder="Select Status"
                      size="small"
                      selectedValue={sessionStatus ?? statusByName.NEW}
                      options={
                        consultId
                          ? (ConsultsMetaData?.updateStatusList ?? [])
                          : (ConsultsMetaData?.createStatusList ?? [])
                      }
                      onChange={(val) => {
                        const statusId = Number(val);
                        setSessionStatus(statusId);
                        if (val === statusByName.CANCELED) {
                          setOpenCancelConsult(true);
                        } else if (statusId === statusByName.COMPLETED) {
                          setOpenCompleteConsult(true);
                        }
                        setValue('sessionStatusId', statusId, {
                          shouldValidate: true,
                        });
                      }}
                      onOptionClick={(val) => {
                        const statusId = Number(val);

                        closeAllPopups();

                        if (activePopupStatus !== statusId) {
                          resetAllConsultValues();
                        }

                        const popupHandlers: Record<number, () => void> = {
                          [statusByName.CANCELED]: () =>
                            setOpenCancelConsult(true),
                          [statusByName.COMPLETED]: () =>
                            setOpenCompleteConsult(true),
                          [statusByName.ON_HOLD]: () =>
                            setOpenOnHoldConsult(true),
                        };

                        popupHandlers[statusId]?.();

                        setActivePopupStatus(statusId);
                      }}
                      disabled={isSessionStatusDisabled}
                      fullWidth
                    />
                  </Box>
                )}
              </Grid2>
              <Grid2 size={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}>
                {DynamicIntakeForm && consultId && (
                  <Box>
                    <CustomTypography
                      id="assigned-to-label"
                      variant="body3"
                      color="textPrimary"
                      className="text-align-left pb-12"
                    >
                      Assigned To&nbsp;
                      {isAssignedToRequired && (
                        <span className="color-red"> *</span>
                      )}
                    </CustomTypography>
                    <Controller
                      name="assignedTo"
                      control={control}
                      shouldUnregister={false}
                      rules={{
                        validate: (value) => {
                          if (isAssignedToRequired && (!value || value === 0)) {
                            return 'Assigned To is required';
                          }
                          return true;
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <>
                          <CustomSelect
                            {...field}
                            id="assign-to"
                            placeholder="Select Assigned To"
                            size="small"
                            selectedValue={field.value ?? 0}
                            options={
                              DynamicIntakeForm?.associatedCliniciansByFacility ??
                              []
                            }
                            onChange={(val) => {
                              const assignedId = Number(val);
                              field.onChange(assignedId);
                              field.onBlur();
                            }}
                            disabled={isAssignedToDisabled}
                            fullWidth
                            displayEmpty
                            emptyOptionLabel="No Assignees"
                          />
                          {fieldState.error && (
                            <FormHelperText error>
                              {fieldState.error.message}
                            </FormHelperText>
                          )}
                        </>
                      )}
                    />
                  </Box>
                )}
              </Grid2>
            </Grid2>
          </Box>
          <Box className="pt-32">
            <CustomDivider />
          </Box>
          <Box className="pt-32">
            {DynamicIntakeFormLoading && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
              >
                <CustomTypography
                  id="loading-text"
                  variant="h5"
                  className="text-align-center pt-32"
                  color="textDisabled"
                >
                  Loading...
                </CustomTypography>
              </Box>
            )}
            {DynamicIntakeForm && (
              <>
                <Box>
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-32"
                        id="patient-information-text"
                      >
                        Patient Information
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={4} className="width-80">
                    {DynamicIntakeForm.sessionFields.map((field) => (
                      <Grid2 key={field.fieldName} size={{ xs: 12, md: 4 }}>
                        <CustomTypography
                          id={`field-label-${field.fieldName}`}
                          variant="body3"
                          color="textPrimary"
                          className="text-align-left pb-12"
                        >
                          {field.fieldLabel}&nbsp;
                          {field.mandatory && (
                            <span className="color-red">*</span>
                          )}
                        </CustomTypography>

                        <FormField
                          field={field}
                          control={control}
                          dateOfBirth={true}
                          minimumAgeRequired={
                            DynamicIntakeForm.minimumAgeRequired
                          }
                        />
                      </Grid2>
                    ))}
                  </Grid2>
                </Box>
                {DynamicIntakeForm.additionalFields.length > 0 && (
                  <>
                    <Box className="pt-32">
                      <CustomDivider />
                    </Box>
                    <Box className="pt-32">
                      <Grid2 container spacing={4} className="width-80">
                        <Grid2
                          size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                        >
                          <CustomTypography
                            variant="h5"
                            className="text-align-left pb-32"
                            id="additional-consult-information-text"
                          >
                            Additional Information
                          </CustomTypography>
                        </Grid2>
                      </Grid2>
                      <Grid2 container spacing={4} className="width-80">
                        {DynamicIntakeForm.additionalFields.map((field) => (
                          <Grid2 key={field.fieldName} size={{ xs: 12, md: 4 }}>
                            <CustomTypography
                              id={`field-label-${field.fieldName}`}
                              variant="body3"
                              color="textPrimary"
                              className="text-align-left pb-12"
                            >
                              {field.fieldLabel}&nbsp;
                              {field.mandatory && (
                                <span className="color-red">*</span>
                              )}
                            </CustomTypography>

                            <FormField field={field} control={control} />
                          </Grid2>
                        ))}
                      </Grid2>
                    </Box>
                  </>
                )}
                {isCompleted && (
                  <>
                    <Box className="pt-32">
                      <CustomDivider />
                    </Box>
                    <Grid2 container>
                      <Grid2 size={{ xs: 12 }} className="pt-32">
                        <Box className="flex align-items-center">
                          <Grid2
                            size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
                          >
                            <Box className="flex align-items-center">
                              <CustomTypography
                                id="consult-observation-title"
                                variant="h5"
                                className="text-align-justify pr-12"
                              >
                                Consult Observation
                              </CustomTypography>
                              <CustomTooltip
                                id="consult-observation-tooltip"
                                title="Edit Consult Observation"
                              >
                                <CustomIconButton
                                  id="edit-icon"
                                  size="small"
                                  icon={<EditOnlyIcon />}
                                  onClick={() => {
                                    handleEditConsultsObservationInfo();
                                  }}
                                />
                              </CustomTooltip>
                              {showConsultObservationMessage && (
                                <CustomTypography
                                  id="consult-observation-message"
                                  variant="body2"
                                  color="success"
                                  className="pl-12"
                                >
                                  (Note: Consult Diagnosis has been recorded.
                                  Click Update to save)
                                </CustomTypography>
                              )}
                            </Box>
                          </Grid2>
                        </Box>
                        <Box className="pt-32">
                          <Box>
                            <Grid2
                              container
                              spacing={0.5}
                              className="text-align-left"
                            >
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="billing-consult-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Billing Consult?
                                </CustomTypography>
                                <CustomTypography
                                  id="billing-consult-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {getDisplayValue(
                                    completeConsultValues.billable ??
                                      apiConsultObservation?.isBillableConsult ??
                                      false
                                  )}
                                </CustomTypography>
                              </Grid2>
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="recomendations-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Recomendations
                                </CustomTypography>
                                <CustomTypography
                                  id="recomendations-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {getRecommendationName()}
                                </CustomTypography>
                              </Grid2>
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="diagnosis-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Diagnosis
                                </CustomTypography>
                                <CustomTypography
                                  id="diagnosis-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {getDiagnosisNames()}
                                </CustomTypography>
                              </Grid2>
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="em-codes-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  E/M Codes
                                </CustomTypography>
                                <CustomTypography
                                  id="em-codes-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {getEmCodeName()}
                                </CustomTypography>
                              </Grid2>
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="follow-up-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Follow-Up
                                </CustomTypography>
                                <CustomTypography
                                  id="follow-up-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {getDisplayValue(getFollowUpValue())}
                                </CustomTypography>
                              </Grid2>
                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="surge-patient-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Surge Patient?
                                </CustomTypography>
                                <CustomTypography
                                  id="surge-patient-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {getDisplayValue(
                                    completeConsultValues.isSurge ??
                                      apiConsultObservation?.isSurge ??
                                      false
                                  )}
                                </CustomTypography>
                              </Grid2>

                              <Grid2
                                size={{
                                  xs: 12,
                                  sm: 12,
                                  md: 4,
                                  lg: 2.4,
                                  xl: 2.4,
                                }}
                              >
                                <CustomTypography
                                  id="ops-user-notes-label"
                                  variant="body3"
                                  color="textDisabled"
                                  className="text-align-left pb-12"
                                >
                                  Ops User Notes
                                </CustomTypography>
                                <CustomTypography
                                  id="surge-patient-value"
                                  variant="body3"
                                  color="textPrimary"
                                  className="text-align-left pb-12"
                                >
                                  {getDisplayValue(
                                    completeConsultValues.opsReasonForCompletion ||
                                      apiConsultObservation?.opsComments
                                  )}
                                </CustomTypography>
                              </Grid2>
                            </Grid2>
                          </Box>
                        </Box>
                      </Grid2>
                    </Grid2>
                  </>
                )}
                <Box className="pt-32">
                  <CustomDivider />
                </Box>
                <Box className="pt-32">
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <CustomTypography
                        variant="h5"
                        className="text-align-left pb-32"
                        id="consult-notes-text"
                      >
                        Consult Notes
                      </CustomTypography>
                    </Grid2>
                  </Grid2>
                  <Grid2 container spacing={4} className="width-80">
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <Controller
                        name="notes"
                        control={control}
                        rules={{
                          maxLength: {
                            value: 1500,
                            message:
                              'Notes must be no more than 1500 characters long.',
                          },
                          validate: (value: string) =>
                            TEXT_FIELD_ALLOWED_CHARACTERS_PATTERN.test(
                              value ?? ''
                            ) ||
                            "Only these special characters are allowed: . , ' + ? @ & _ ( ) / : ; -",
                        }}
                        render={({ field, fieldState }) => (
                          <>
                            <CustomTextField
                              {...field}
                              id="txt-consult-notes"
                              placeholder="Type Consult Notes"
                              size="small"
                              multiLine
                              rows={4}
                              value={field.value || ''}
                              onChange={(event) => {
                                field.onChange(event.target.value);
                              }}
                            />
                            {fieldState.error && (
                              <CustomTypography
                                id="validate-consult-notes"
                                color="error"
                                variant="body3"
                                className="pt-8 text-align-left"
                              >
                                {fieldState.error.message}
                              </CustomTypography>
                            )}
                          </>
                        )}
                      />
                    </Grid2>
                  </Grid2>
                </Box>

                <Box className="pt-32">
                  <Grid2 container>
                    <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                      <Box className="display-flex justify-end">
                        <Box className="pr-12">
                          <CustomButton
                            id="cancel-button"
                            onClick={() => {
                              handleOpenConsultConfirmDialog(true);
                            }}
                            variant="outlined"
                          >
                            Cancel
                          </CustomButton>
                        </Box>
                        <Box>
                          <CustomButton
                            id="save-button"
                            type="submit"
                            onClick={() => {
                              setConsultSubmitAction(
                                consultId ? 'Update' : 'Save'
                              );
                            }}
                            disabled={isSubmitting}
                            variant="contained"
                          >
                            {buttonText}
                          </CustomButton>
                        </Box>
                      </Box>
                    </Grid2>
                  </Grid2>
                </Box>
              </>
            )}
          </Box>
        </form>
      </Box>

      <Box>
        <Box>
          <ConfirmDiscardDialog
            open={openConsultConfirmDialog}
            onClose={() => handleOpenConsultConfirmDialog(false)}
            navigateTo={
              consultId && !location.state?.isConsultList
                ? `${CONSULTS_MORE_DETAILS_ROUTE_PATH}/${consultId}`
                : CONSULTS_ROUTE_PATH
            }
          />
        </Box>
      </Box>
      <Box>
        <CustomDialog
          id="facility-change-dialog"
          open={showFacilityConfirm}
          onClose={cancelFacilityChange}
          disableEscapeKeyDown
        >
          <CustomDialogContent>
            <Box className="pb-8">
              <CustomTypography id="facility-change-title" variant="h4">
                Change Facility
              </CustomTypography>
            </Box>
            <Box>
              <CustomTypography
                id="facility-change-text"
                variant="body3"
                color="textDisabled"
              >
                Changing the facility will reset consult-related information
                that may have already been entered. Do you wish to proceed?
              </CustomTypography>
            </Box>
          </CustomDialogContent>

          <CustomDialogActions>
            <CustomButton
              id="cancel-button"
              variant="outlined"
              color="primary"
              onClick={cancelFacilityChange}
            >
              Cancel
            </CustomButton>

            <CustomButton
              id="proceed-button"
              variant="contained"
              color="primary"
              onClick={() => applyFacilityChange(pendingFacilityId)}
            >
              Proceed
            </CustomButton>
          </CustomDialogActions>
        </CustomDialog>
      </Box>
    </Box>
  );
};

export default AddNewConsult;
